import { Fragment, useMemo } from "react"
import {
  ChangeSectionButton,
  CurrentMonth,
  DateButton,
  DateButtonText,
  HeaderWrapper,
  NavigateButton,
  StyledTd,
  StyledTh,
  StyledThR,
  StyledTr,
  TodayButtonDot,
  NavigationButtonWrapper,
  TableWrapper,
} from "./styled"
import dayjs, { Dayjs } from "dayjs"
import utc from "dayjs/plugin/utc"
import timeZone from "dayjs/plugin/timezone"
import LeftIcon from "./Icons/LeftIcon"
import RightIcon from "./Icons/RightIcon"
import { FlexBox } from "components/Common/FlexBox"
import { isDateUnder16YearOld } from "./birthdateValidators"

enum IDisplaySection {
  SELECT_DATE = "SELECT_DATE",
  SELECT_MONTH = "SELECT_MONTH",
  SELECT_YEAR = "SELECT_YEAR",
}
dayjs.extend(utc)
dayjs.extend(timeZone)

const Header = ({
  month,
  setMonth,
  setSection,
}: {
  month: Dayjs
  setMonth: (month: Dayjs) => void
  setSection: (section: IDisplaySection) => void
}) => {
  return (
    <HeaderWrapper>
      <CurrentMonth>
        <ChangeSectionButton
          onClick={() => setSection(IDisplaySection.SELECT_MONTH)}
        >
          {month.format("MMMM")}
        </ChangeSectionButton>{" "}
        <ChangeSectionButton
          onClick={() => setSection(IDisplaySection.SELECT_YEAR)}
        >
          {month.format("YYYY")}
        </ChangeSectionButton>
      </CurrentMonth>

      <FlexBox>
        <NavigationButtonWrapper>
          <NavigateButton
            style={{ width: 24, height: 24 }}
            onClick={() => setMonth(month.subtract(1, "month"))}
          >
            <LeftIcon />
          </NavigateButton>
        </NavigationButtonWrapper>

        <NavigationButtonWrapper>
          <NavigateButton
            style={{ width: 24, height: 24 }}
            onClick={() => setMonth(month.add(1, "month"))}
          >
            <RightIcon />
          </NavigateButton>
        </NavigationButtonWrapper>
      </FlexBox>
    </HeaderWrapper>
  )
}

const getArraySeven = () => Array(7).fill(0)

const TableHead = () => {
  return (
    <thead>
      <StyledThR>
        {getArraySeven().map((_, index) => (
          <StyledTh key={index}>{dayjs().day(index).format("ddd")}</StyledTh>
        ))}
      </StyledThR>
    </thead>
  )
}

const Date = ({
  day,
  month,
  value,
  onChange,
}: {
  day: Dayjs
  month: Dayjs
  value?: Dayjs
  onChange?: (day: Dayjs) => void
}) => {
  const isInDifferentMonth =
    day.isBefore(month.startOf("month")) || day.isAfter(month.endOf("month"))

  const isAvailable = false
  const isToday = day.isSame(dayjs().startOf("day"))
  const isSelected = value && value.startOf("day").isSame(day)
  const isDisabled = isDateUnder16YearOld(day)

  return (
    <StyledTd>
      <DateButton
        $isDisabled={isDisabled}
        $available={!isInDifferentMonth}
        $isSelected={!!isSelected}
        $isToday={isToday}
        onClick={() => {
          if (!isDisabled) {
            onChange?.(day)
          }
        }}
      >
        <DateButtonText
          className="date_btn__text"
          $available={!isInDifferentMonth}
          $isSelected={!!isSelected}
          $isToday={isToday}
        >
          {day.get("date")}
        </DateButtonText>
        {isAvailable && (
          <TodayButtonDot
            className="date_btn__dot"
            $isSelected={!!isSelected}
            $isInDifferentMonth={isInDifferentMonth}
          />
        )}
      </DateButton>
    </StyledTd>
  )
}

const WeekRow = ({
  sunday,
  value,
  onChange,
  month,
}: {
  sunday: Dayjs
  value?: Dayjs
  month: Dayjs
  onChange?: (day: Dayjs) => void
}) => {
  return (
    <StyledTr>
      {getArraySeven().map((_, index) => (
        <Date
          month={month}
          key={index}
          day={sunday.add(index, "day")}
          value={value}
          onChange={onChange}
        />
      ))}
    </StyledTr>
  )
}

interface IDateSelectionProps {
  month: Dayjs
  setMonth: (month: Dayjs) => void
  setSection: (section: IDisplaySection) => void
  value?: Dayjs
  onChange?: (day: Dayjs) => void
}

const DateSelection = (props: IDateSelectionProps) => {
  const { month, setMonth, setSection, value, onChange } = props
  const startDate = month.startOf("month").startOf("week")

  const weeks = useMemo(() => {
    const weeks = []
    let done = false
    let sunday = startDate
    let monthIndex = month.month()
    let count = 0

    while (!done) {
      weeks.push(
        <WeekRow
          key={sunday.toString()}
          sunday={sunday}
          value={value}
          month={month}
          onChange={onChange}
        />,
      )
      sunday = sunday.add(1, "week")
      done = count++ > 2 && monthIndex !== sunday.month()
      monthIndex = sunday.month()
    }

    return weeks
  }, [month, value, timeZone])

  return (
    <Fragment>
      <Header month={month} setMonth={setMonth} setSection={setSection} />

      <TableWrapper>
        <table style={{ width: "100%" }}>
          <TableHead />
          <tbody>{weeks}</tbody>
        </table>
      </TableWrapper>
    </Fragment>
  )
}

export default DateSelection
